import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext, useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatDay } from '../../lib/time';
import { ArchiveIcon, PauseIcon, PencilIcon, PlayIcon } from '../../ui/icons';
import { Avatar, Notice, Pill } from '../../ui/parts';
import {
  useAdminDirections, useAdminSpecialists, useCreateSpecialist, useStaffInviteLink,
  useResumeSpecialist, useTransferSpecialist, useUpdateSpecialist,
} from '../api';
import { ArchiveSheet, PauseSheet } from '../PauseSheets';
import type { AdminSpecialist } from '../types';

/** Вкладка «Люди»: специалисты центра. */
export function Staff() {
  const writable = useWritable();
  const navigate = useNavigate();
  const sheet = useSheet();
  const specialists = useAdminSpecialists();
  const directions = useAdminDirections();

  useScreen('Специалисты', null);

  // Архивных не прячем: карточка не исчезла, она просто больше не в работе.
  // Порядок задаёт сервер — архивные идут в конец.
  const list = specialists.data ?? [];
  const active = list.filter((s) => s.status === 'active');
  const archived = list.filter((s) => s.status === 'archived');
  const dirTitle = (id: number) => directions.data?.find((d) => d.id === id)?.title ?? '';
  /** Все категории строкой: совмещающего надо узнавать, не открывая карточку. */
  const dirTitles = (ids: number[]) => ids.map(dirTitle).filter(Boolean).join(' · ');

  return (
    <Screen>
      <div className="sechead">
        <p className="eyebrow">Специалисты организации</p>
        <span className="lead" style={{ fontSize: 13 }}>
          {active.length} активных{archived.length ? ` · ${archived.length} в архиве` : ''}
        </span>
      </div>

      {specialists.isPending ? <p className="lead">Загружаем…</p> : null}

      <div className="rows">
        {list.map((s) => (
          <button
            className={`rw${s.status === 'archived' ? ' is-archived' : ''}`}
            key={s.id}
            onClick={() => sheet.open(<StaffSheet specialist={s} directionTitle={dirTitles(s.direction_ids)} />)}
          >
            <Avatar name={s.display_name} photo={s.photo_url} small />
            <span className="rw-body">
              <b>{s.display_name}</b>
              <span>{dirTitles(s.direction_ids)}{s.title ? ` · ${s.title}` : ''}</span>
            </span>
            {s.status === 'archived' ? <Pill kind="done">архив</Pill> : null}
            {s.status === 'paused' ? <Pill kind="off">пауза</Pill> : null}
            {s.status === 'active' ? <Pill kind="paid">активен</Pill> : null}
          </button>
        ))}
      </div>

      {writable ? (
        <button className="add" onClick={() => navigate('/admin/staff/add')}>＋ Добавить специалиста</button>
      ) : null}

      <p className="footnote">Специалист настраивает своё расписание сам — вы можете открыть и поправить</p>
    </Screen>
  );
}

function StaffSheet({ specialist, directionTitle }: { specialist: AdminSpecialist; directionTitle: string }) {
  const me = useMeContext();
  const writable = useWritable();
  const navigate = useNavigate();
  const sheet = useSheet();
  const toast = useToast();
  const resume = useResumeSpecialist();
  const invite = useStaffInviteLink();

  const go = (path: string) => {
    sheet.close();
    navigate(path);
  };

  return (
    <>
      <h3>{specialist.display_name}</h3>
      <p>{directionTitle}{specialist.title ? ` · ${specialist.title}` : ''}</p>

      <div className="kv">
        <div>
          <span className="k">Состояние</span>
          <span className="v">
            {specialist.status === 'paused'
              ? `пауза${specialist.paused_until ? ` до ${formatDay(specialist.paused_until, me.center.tz)}` : ' бессрочно'}`
              : 'ведёт запись'}
          </span>
        </div>
        <div>
          <span className="k">Услуг назначено</span>
          <span className="v">{specialist.rates}</span>
        </div>
        <div>
          <span className="k">Будущих записей</span>
          <span className="v">{specialist.upcoming}</span>
        </div>
        <div>
          <span className="k">Telegram</span>
          <span className="v">
            <span>{specialist.tg_id ? 'привязан' : 'ещё не входил'}</span>
            {/* Ссылку, выданную при создании, теряют чаще всего — здесь её берут заново. */}
            {!specialist.tg_id && writable ? (
              <button
                type="button"
                disabled={invite.isPending}
                onClick={() =>
                  invite.mutate(specialist.id, {
                    onSuccess: (res) => {
                      void navigator.clipboard?.writeText(`https://${res.link}`);
                      toast('Ссылка скопирована — отправьте её специалисту', { ms: 4000 });
                    },
                    onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось собрать ссылку'),
                  })
                }
              >
                {invite.isPending ? 'собираем…' : 'взять ссылку'}
              </button>
            ) : null}
            {/* Карточку ведёт другой человек или у прежнего сменился Telegram —
                вход меняется, карточка остаётся. */}
            {specialist.tg_id && writable ? (
              <button type="button" onClick={() => sheet.open(<TransferSheet specialist={specialist} />)}>
                передать
              </button>
            ) : null}
          </span>
        </div>
      </div>

      {specialist.status === 'paused' && specialist.paused_reason ? (
        <Notice kind="calm">Причина паузы: {specialist.paused_reason}</Notice>
      ) : null}

      <div className="row-actions">
        <button className="btn" onClick={() => go(`/admin/staff/${specialist.id}/services`)}>Услуги и ставки</button>
        <button className="btn" onClick={() => go(`/admin/staff/${specialist.id}/schedule`)}>Расписание</button>
      </div>


      {writable && specialist.status === 'archived' ? (
        <>
          <Notice kind="calm">
            Карточка в архиве: записаться к специалисту нельзя, а история записей и расчёты целы.
          </Notice>
          <button
            className="btn solid"
            disabled={resume.isPending}
            onClick={() =>
              resume.mutate(specialist.id, {
                onSuccess: () => {
                  toast('Специалист вернулся из архива');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
              })
            }
          >
            Вернуть из архива
          </button>
        </>
      ) : null}

      {/* Три редких действия — строкой иконок: полноразмерными кнопками они
          занимали половину шторки, а нужны раз в несколько месяцев. Подпись под
          иконкой оставлена: «архив» и «пауза» по одной картинке не различить,
          а цена ошибки здесь — снятый с записи специалист. */}
      {writable && specialist.status !== 'archived' ? (
        <div className="icon-actions">
          <button
            className="ico-act"
            type="button"
            onClick={() => sheet.open(<EditSheet specialist={specialist} />)}
          >
            <PencilIcon />
            <span>Правка</span>
          </button>

          {specialist.status === 'paused' ? (
            <button
              className="ico-act"
              type="button"
              disabled={resume.isPending}
              onClick={() =>
                resume.mutate(specialist.id, {
                  onSuccess: () => {
                    toast('Запись возобновлена');
                    sheet.close();
                  },
                  onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
                })
              }
            >
              <PlayIcon />
              <span>Возобновить</span>
            </button>
          ) : (
            <button
              className="ico-act"
              type="button"
              onClick={() => sheet.open(<PauseSheet specialist={specialist} />)}
            >
              <PauseIcon />
              <span>Пауза</span>
            </button>
          )}

          <button
            className="ico-act is-danger"
            type="button"
            onClick={() => sheet.open(<ArchiveSheet specialist={specialist} />)}
          >
            <ArchiveIcon />
            <span>В архив</span>
          </button>
        </div>
      ) : null}

      <button className="btn" onClick={() => sheet.close()}>Закрыть</button>
    </>
  );
}

/**
 * Карточка специалиста: имя, специализация, фото, роль. Направление здесь не
 * меняется — за ним тянутся закрепления детей, услуги и ставки.
 */
function EditSheet({ specialist }: { specialist: AdminSpecialist }) {
  const sheet = useSheet();
  const toast = useToast();
  const update = useUpdateSpecialist();
  const [name, setName] = useState(specialist.display_name);
  const [title, setTitle] = useState(specialist.title);
  const [photo, setPhoto] = useState(specialist.photo_url ?? '');
  const [isAdmin, setIsAdmin] = useState(!!specialist.is_admin);
  const [dirIds, setDirIds] = useState<number[]>(specialist.direction_ids);
  const directions = useAdminDirections();
  const cats = (directions.data ?? []).filter((d) => d.is_active || dirIds.includes(d.id));

  const photoOk = !photo.trim() || /^https:\/\/\S+$/.test(photo.trim());
  const ready = name.trim().length > 2 && photoOk && dirIds.length > 0;

  return (
    <>
      <h3>Правка карточки</h3>
      <div className="field">
        <label htmlFor="edName">Имя и фамилия</label>
        <input id="edName" type="text" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="edTitle">Специализация</label>
        <input id="edTitle" type="text" placeholder="Дети 5–12 лет, коррекция" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="edPhoto">Фото</label>
        <input id="edPhoto" type="url" placeholder="https://…/photo.jpg" value={photo} onChange={(e) => setPhoto(e.target.value)} />
        <span className="hint">
          Ссылка на картинку — файлы мы не храним. Пусто — родитель увидит инициалы.
          {photoOk ? '' : ' Ссылка должна начинаться с https://'}
        </span>
      </div>

      {photo.trim() && photoOk ? (
        <div className="rw">
          <Avatar name={name} photo={photo.trim()} small />
          <span className="rw-body">
            <b>Так увидит родитель</b>
            <span>Битую ссылку заменим инициалами</span>
          </span>
        </div>
      ) : null}

      <div className="field">
        <label>Категории</label>
        <CategoryPicker all={cats} value={dirIds} onChange={setDirIds} />
        <span className={dirIds.length ? 'hint' : 'err'}>
          {dirIds.length
            ? `Главная — «${cats.find((d) => d.id === dirIds[0])?.title ?? ''}»`
            : 'Нужна хотя бы одна категория'}
        </span>
      </div>

      <label className="check">
        <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
        <span>Ещё и администратор организации</span>
      </label>
      {isAdmin && !specialist.tg_id ? (
        <span className="hint">Права появятся, когда он войдёт по своей ссылке — до этого входить в админку некому.</span>
      ) : null}

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!ready || update.isPending}
          onClick={() =>
            update.mutate(
              {
                id: specialist.id, displayName: name.trim(), title: title.trim(),
                photoUrl: photo.trim(), isAdmin, directionIds: dirIds,
              },
              {
                onSuccess: () => {
                  toast('Карточка сохранена');
                  sheet.close();
                },
                // Убрать категорию мешают ставки и закрепления — сервер называет
                // их поимённо, и этот текст нужно дочитать до конца.
                onError: (e) => toast(
                  e instanceof ApiError ? e.message : 'Не получилось сохранить',
                  { ms: e instanceof ApiError && e.is('DIRECTION_HAS_RATES', 'DIRECTION_HAS_CLIENTS') ? 6000 : undefined },
                ),
              },
            )
          }
        >
          Сохранить
        </button>
      </div>
    </>
  );
}

/**
 * Выбор категорий. Их несколько: логопед, который ведёт ещё и нейрокоррекцию,
 * раньше заводился двумя карточками — с двумя расписаниями и двумя лицами
 * в списке у клиента.
 *
 * Порядок значим: первая выбранная становится главной. По ней специалист
 * сортируется в списках и попадает в отчёт по выплатам, где строка на человека,
 * а не на услугу.
 */
function CategoryPicker({ all, value, onChange, disabled }: {
  all: { id: number; title: string }[];
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="chips">
      {all.map((d) => {
        const on = value.includes(d.id);
        return (
          <button
            key={d.id}
            className="chip"
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onChange(on ? value.filter((x) => x !== d.id) : [...value, d.id])}
          >
            {d.title}
          </button>
        );
      })}
    </div>
  );
}

/** Подэкран «Новый специалист»: приглашение уходит ссылкой в боте. */
export function StaffAdd() {
  const navigate = useNavigate();
  const toast = useToast();
  const sheet = useSheet();
  const directions = useAdminDirections();
  const create = useCreateSpecialist();

  const [name, setName] = useState('');
  const [tg, setTg] = useState('');
  const [dirIds, setDirIds] = useState<number[] | null>(null);
  const [title, setTitle] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const list = (directions.data ?? []).filter((d) => d.is_active);
  // Пока не выбрали — первая категория: у большинства она и останется единственной.
  const chosen = dirIds ?? (list[0] ? [list[0].id] : []);
  const ready = name.trim().length > 2 && chosen.length > 0;

  useScreen('Новый специалист', null);

  return (
    <Screen>
      <div>
        <p className="eyebrow">Новый специалист</p>
        <p className="lead" style={{ marginTop: 6 }}>
          После сохранения он получит ссылку-приглашение в боте и настроит часы сам.
        </p>
      </div>

      <div className="field">
        <label htmlFor="nName">Имя и фамилия</label>
        <input id="nName" type="text" placeholder="Игорь Северин" value={name} onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="nTg">Telegram</label>
        <input id="nTg" type="text" placeholder="@severin" value={tg} onChange={(e) => setTg(e.target.value)} />
        <span className="hint">По нему привяжем аккаунт при первом входе.</span>
      </div>

      <div className="field">
        <label>Категории</label>
        <CategoryPicker all={list} value={chosen} onChange={setDirIds} />
        <span className="hint">
          {chosen.length > 1
            ? `Можно несколько. Главная — «${list.find((d) => d.id === chosen[0])?.title ?? ''}»`
            : 'Можно выбрать несколько — специалист появится в записи по каждой'}
        </span>
      </div>

      <div className="field">
        <label htmlFor="nSpec">Специализация</label>
        <input id="nSpec" type="text" placeholder="Дети 5–12 лет, коррекция" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="nRole">Роль</label>
        <select id="nRole" value={isAdmin ? '1' : '0'} onChange={(e) => setIsAdmin(e.target.value === '1')}>
          <option value="0">Специалист</option>
          <option value="1">Специалист + администратор</option>
        </select>
      </div>

      <Notice kind="calm">
        Услуги и ставки назначаются после создания — на карточке специалиста. Без них он не появится
        в записи, даже если задаст часы.
      </Notice>

      <button
        className="btn solid"
        disabled={!ready || create.isPending}
        onClick={() =>
          create.mutate(
            { directionIds: chosen, displayName: name.trim(), title: title.trim(), isAdmin, tgUsername: tg.trim() || undefined },
            {
              onSuccess: (res) => sheet.open(<InviteSheet name={name.trim()} link={res.inviteLink} onClose={() => navigate('/admin/staff')} />),
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось создать'),
            },
          )
        }
      >
        {create.isPending ? 'Создаём…' : 'Отправить приглашение'}
      </button>
    </Screen>
  );
}

/**
 * Передача карточки другому Telegram: специалиста сменил человек или у прежнего
 * сменился аккаунт. Карточка остаётся той же — записи, ставки, закрепления и
 * история никуда не переезжают, меняется только тот, кто в неё входит.
 *
 * Подтверждение и готовая ссылка живут в одной шторке: мутация, открывшая
 * следующую, потеряла бы свой колбэк вместе с ссылкой — а второй раз её уже
 * не выдать, прежний владелец к этому моменту отрезан.
 */
function TransferSheet({ specialist }: { specialist: AdminSpecialist }) {
  const sheet = useSheet();
  const toast = useToast();
  const navigate = useNavigate();
  const transfer = useTransferSpecialist();
  const [link, setLink] = useState<string | null>(null);

  if (link) {
    return (
      <>
        <h3>Карточка передана</h3>
        <p>
          Прежний аккаунт доступ уже потерял — мы предупредили его сообщением в боте. Перешлите
          ссылку новому владельцу: по ней он войдёт в ту же карточку, со всеми записями и ставками.
        </p>
        <div className="field">
          <label htmlFor="trLink">Ссылка для входа</label>
          <input id="trLink" type="text" readOnly value={`https://${link}`} onFocus={(e) => e.currentTarget.select()} />
        </div>
        <button
          className="btn"
          onClick={() => {
            void navigator.clipboard?.writeText(`https://${link}`);
            toast('Ссылка скопирована');
          }}
        >
          Скопировать ссылку
        </button>
        <button className="btn solid" onClick={() => sheet.close()}>Готово</button>
      </>
    );
  }

  return (
    <>
      <h3>Передать другому Telegram</h3>
      <p>
        Карточка «{specialist.display_name}» останется прежней: записи, расписание, ставки и
        закрепления никуда не денутся. Сменится только аккаунт, который в неё входит.
      </p>
      <Notice>
        Прежний аккаунт потеряет доступ сразу, ещё до того как войдёт новый. Ссылку для входа
        покажем следом — отправьте её сами: бот не может написать первым тому, кто его не открывал.
      </Notice>
      {specialist.is_admin ? (
        <Notice kind="calm">
          Права администратора остаются у прежнего человека: админка — про человека, а не про
          карточку. Снимите их на экране «Администраторы», если он уходит совсем; новому владельцу
          права выдаются отдельно, галочкой в правке карточки.
        </Notice>
      ) : null}
      <button
        className="btn solid"
        disabled={transfer.isPending}
        onClick={() =>
          transfer.mutate(specialist.id, {
            onSuccess: (res) => setLink(res.link),
            onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось передать карточку'),
          })
        }
      >
        {transfer.isPending ? 'Передаём…' : 'Передать и получить ссылку'}
      </button>
      {specialist.is_admin ? (
        <button
          className="btn"
          onClick={() => {
            sheet.close();
            navigate('/admin/org/admins');
          }}
        >
          Открыть «Администраторы»
        </button>
      ) : null}
      <button className="btn" onClick={() => sheet.close()}>Отмена</button>
    </>
  );
}

/**
 * Ссылка-приглашение. Бот не может написать первым тому, кто ему не писал, —
 * поэтому ссылку админ передаёт сам.
 */
function InviteSheet({ name, link, onClose }: { name: string; link: string | null; onClose: () => void }) {
  const sheet = useSheet();
  const toast = useToast();

  return (
    <>
      <h3>{name} добавлен</h3>
      {link ? (
        <>
          <p>
            Перешлите эту ссылку — по ней специалист войдёт в приложение и настроит часы.
            Бот не может написать ему первым: Telegram разрешает это только тем, кто сам открыл бота.
          </p>
          <div className="field">
            <label htmlFor="invLink">Ссылка-приглашение</label>
            <input id="invLink" type="text" readOnly value={`https://${link}`} onFocus={(e) => e.currentTarget.select()} />
          </div>
          <button
            className="btn"
            onClick={() => {
              void navigator.clipboard?.writeText(`https://${link}`);
              toast('Ссылка скопирована');
            }}
          >
            Скопировать ссылку
          </button>
        </>
      ) : (
        <Notice>
          У организации не настроен бот, поэтому ссылку собрать не из чего. Подключите бота — и
          приглашение появится на карточке специалиста.
        </Notice>
      )}
      <button
        className="btn solid"
        onClick={() => {
          sheet.close();
          onClose();
        }}
      >
        Готово
      </button>
    </>
  );
}
